/**
 * Monaco completion provider for Python.
 *
 * Python has no built-in Monaco language service, so we roll a lightweight
 * provider ourselves. Coverage: ~80 common builtins + str/list/dict/set
 * method completions sourced from python-stdlib.ts.
 *
 * Behavior:
 *   - On alpha-character trigger or Ctrl+Space, we return global builtins +
 *     constants. Monaco filters client-side as the user keeps typing.
 *   - On `.` trigger, we return method completions (union across common
 *     types). The detail field prefixes "(str)", "(list)", etc. so the user
 *     can tell duplicates apart where they exist.
 *
 * Returns a disposable so callers can unregister when the language changes.
 */
import type { Monaco } from '@monaco-editor/react';
import type { languages, editor, Position, IRange } from 'monaco-editor';

import { PY_GLOBALS, PY_METHODS, type PyCompletion, type Origin } from './python-stdlib.js';

export function registerPythonCompletion(monaco: Monaco): { dispose: () => void } {
  return monaco.languages.registerCompletionItemProvider('python', {
    triggerCharacters: ['.'],
    provideCompletionItems: (model: editor.ITextModel, position: Position) =>
      buildCompletions(monaco, model, position),
  });
}

function buildCompletions(
  monaco: Monaco,
  model: editor.ITextModel,
  position: Position,
): languages.ProviderResult<languages.CompletionList> {
  // Range the completion will replace — just the word the user is typing.
  const word = model.getWordUntilPosition(position);
  const range: IRange = {
    startLineNumber: position.lineNumber,
    endLineNumber: position.lineNumber,
    startColumn: word.startColumn,
    endColumn: word.endColumn,
  };

  // Look at the character immediately before the word we're completing.
  // If it's `.`, this is an attribute/method access; otherwise global scope.
  const charBeforeWord = charAt(model, position.lineNumber, word.startColumn - 1);
  const isDotAccess = charBeforeWord === '.';

  const entries = isDotAccess ? PY_METHODS : PY_GLOBALS;
  const suggestions: languages.CompletionItem[] = entries.map((e) =>
    toMonacoItem(monaco, e, range),
  );

  return { suggestions };
}

function toMonacoItem(monaco: Monaco, e: PyCompletion, range: IRange): languages.CompletionItem {
  return {
    label: e.label,
    kind: monacoKind(monaco, e.origin),
    detail: annotateDetail(e),
    documentation: { value: e.documentation },
    insertText: e.insertText,
    insertTextRules: hasSnippetCursor(e.insertText)
      ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
      : undefined,
    range,
  };
}

function annotateDetail(e: PyCompletion): string {
  // Prefix the origin for methods so the user can disambiguate duplicates
  // (e.g. `.pop` exists on both list and dict with different signatures).
  if (e.origin === 'str' || e.origin === 'list' || e.origin === 'dict' || e.origin === 'set') {
    return `${e.origin} • ${e.detail}`;
  }
  return e.detail;
}

function monacoKind(monaco: Monaco, origin: Origin): languages.CompletionItemKind {
  switch (origin) {
    case 'constant':
      return monaco.languages.CompletionItemKind.Constant;
    case 'builtin':
      return monaco.languages.CompletionItemKind.Function;
    case 'str':
    case 'list':
    case 'dict':
    case 'set':
      return monaco.languages.CompletionItemKind.Method;
  }
}

function hasSnippetCursor(s: string): boolean {
  return s.includes('$0') || /\$\{?\d/.test(s);
}

/**
 * Return the character at (lineNumber, column). Column is 1-indexed in
 * Monaco. Returns '' if out of range.
 */
function charAt(model: editor.ITextModel, lineNumber: number, column: number): string {
  if (column < 1) return '';
  const line = model.getLineContent(lineNumber);
  if (column > line.length) return '';
  return line.charAt(column - 1);
}
